<script>
import BaseScheduledTasks from './ScheduledTasks.vue'
import notificationTaskGroupApi from '@/api/notificationTaskGroup'
import NotificationTaskSettingsRouterDialog from '@/components/NotificationTaskSettingsRouterDialog.vue'

function correctedDefinition(definition) {
  if (definition.groupId === 'market_event_watch') {
    return {
      ...definition,
      description: '聚合监管、宏观、指数、资金及系统性行业等全市场重大事件。',
      expectedModules: ['news_watch']
    }
  }
  if (definition.groupId === 'stock_event_watch') {
    return {
      ...definition,
      description: '聚合第一本账公司的公告、个股新闻与董秘问答。',
      expectedModules: ['announcement_watch', 'stock_news_watch', 'irm_qa_watch']
    }
  }
  return definition
}

const TIMEOUT_CODES = ['CHILD_TERMINAL_TIMEOUT']
const TERMINAL_CONTRACT_CODES = [
  'CHILD_HANDLER_TERMINAL_CONTRACT',
  'CHILD_HANDLER_EXECUTION_FAILED',
  'CHILD_HANDLER_FAILURE_PERSISTENCE'
]

export default {
  name: 'ScheduledTasksParentRuntime',
  extends: BaseScheduledTasks,
  components: {
    NotificationTaskSettingsPreviewDialog: NotificationTaskSettingsRouterDialog
  },
  data() {
    return {
      ownerStatus: null,
      ownerStatusLoadFailed: false
    }
  },
  methods: {
    async refreshData() {
      await Promise.all([
        BaseScheduledTasks.methods.refreshData.call(this),
        this.loadOwnerStatus()
      ])
    },
    async loadOwnerStatus() {
      try {
        const response = await notificationTaskGroupApi.getOwnerStatus()
        this.ownerStatus = this.unwrapResponse(response)
        this.ownerStatusLoadFailed = false
      } catch (error) {
        this.ownerStatus = null
        this.ownerStatusLoadFailed = true
      }
    },
    isTerminalParent(run) {
      return ['success', 'partial_success', 'failed'].includes(String(run && run.status || '').toLowerCase())
    },
    parentErrorCode(run) {
      return String(run && run.error_code || '').toUpperCase()
    },
    buildGroupCard(definition) {
      const def = correctedDefinition(definition)
      const card = BaseScheduledTasks.methods.buildGroupCard.call(this, def)
      const summary = this.summaryGroup(def.groupId)
      const projected = summary && (summary.display_latest_run || summary.latest_parent_run)
      if (projected && projected.is_parent_result === true) {
        card.latestRunTime = this.displayTime(this.parentRunTimeValue(projected))
        card.latestResultText = this.parentResultWithCounts(projected)
      }

      if (projected && this.isTerminalParent(projected) && card.runtimeStatus === 'running') {
        card.runtimeStatus = 'parent_terminal_conflict'
        card.runtimeStatusText = TIMEOUT_CODES.includes(this.parentErrorCode(projected))
          ? '父任务已超时，存在未收尾子记录'
          : '父任务已结束，存在未收尾子记录'
      } else if (def.groupId !== 'position_context_watch') {
        if (this.ownerStatus && this.ownerStatus.ready === true) {
          if (card.runtimeStatus !== 'running') {
            const actual = Number(this.ownerStatus.actual_job_count || 0)
            const expected = Number(this.ownerStatus.expected_job_count || 0)
            card.runtimeStatus = 'owner_ready'
            card.runtimeStatusText = `调度器在线 · ${actual}/${expected} Job`
          }
        } else if (!this.ownerStatusLoadFailed) {
          card.runtimeStatus = 'owner_offline'
          card.runtimeStatusText = '调度未接管'
        }
      }
      return card
    },
    parentResultWithCounts(run) {
      const errorCode = this.parentErrorCode(run)
      const failed = Number(run && run.failed_count || 0)
      if (TIMEOUT_CODES.includes(errorCode)) {
        return `执行超时，${failed || Number(run && run.planned_module_count || 0)} 个模块未在终态等待窗口内完成`
      }
      if (TERMINAL_CONTRACT_CODES.includes(errorCode)) {
        return `子模块终态异常，失败 ${failed || 1} 个；请查看父运行详情`
      }
      const base = BaseScheduledTasks.methods.parentResultText.call(this, run)
      const added = Number(run && run.new_items_count)
      const deduped = Number(run && run.deduped_count)
      const parts = []
      if (Number.isFinite(added)) parts.push(`新增 ${added} 条`)
      if (Number.isFinite(deduped) && deduped > 0) parts.push(`重复过滤 ${deduped} 条`)
      return parts.length ? `${base}，${parts.join('，')}` : base
    },
    parentResultText(run) {
      return this.parentResultWithCounts(run)
    },
    groupScopeSummary(groupId, tasks) {
      if (groupId === 'market_event_watch') return '全市场重大事件（系统管理）'
      if (groupId === 'stock_event_watch') {
        return this.firstBook.available
          ? `第一本账全范围，当前 ${this.firstBook.allCount} 只`
          : '第一本账全范围，数量暂不可用'
      }
      return BaseScheduledTasks.methods.groupScopeSummary.call(this, groupId, tasks)
    },
    moduleLabel(taskType) {
      if (taskType === 'announcement_watch') return '公司公告'
      return BaseScheduledTasks.methods.moduleLabel.call(this, taskType)
    },
    childResultText(child) {
      if (!child) return '结果暂不可用'
      const status = String(child.child_status || '').toLowerCase()
      const errorCode = String(child.error_code || '').toUpperCase()
      if (TIMEOUT_CODES.includes(errorCode)) return '父任务等待超时，子模块已按精确运行编号收尾'
      if (TERMINAL_CONTRACT_CODES.includes(errorCode)) return '子处理器未形成可靠终态，已失败收尾'
      if (status !== 'success') {
        return BaseScheduledTasks.methods.childResultText.call(this, child)
      }
      if (child.task_type === 'system_health_check') return '系统正常；通知由父任务汇总发送'
      const added = Number(child.new_items_count || 0)
      const deduped = Number(child.deduped_count || 0)
      return `新增 ${added}，重复过滤 ${deduped}；子模块不单独通知`
    },
    detailScopeText(parent) {
      const snapshot = parent && parent.config_snapshot
      if (!snapshot || typeof snapshot !== 'object') return '范围暂不可用'
      if (snapshot.scope_type === 'system_managed') return '全市场重大事件（系统管理）'
      if (snapshot.scope_type === 'first_book_all') return '第一本账全范围'
      return BaseScheduledTasks.methods.detailScopeText.call(this, parent)
    },
    runtimeStatusType(status) {
      if (status === 'owner_ready') return 'success'
      if (status === 'owner_offline' || status === 'parent_terminal_conflict') return 'danger'
      return BaseScheduledTasks.methods.runtimeStatusType.call(this, status)
    }
  }
}
</script>
